import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import KundenAuswahl from "@/components/KundenAuswahl";
import { toast } from "sonner";
import { Loader2, ListPlus } from "lucide-react";

/**
 * Aufgabe anlegen – von jeder Seite aus, in drei Feldern.
 *
 * Warum das nötig war: Aufgaben ließen sich bisher nur in der Kundenakte
 * anlegen, ganz unten. Wer am Telefon sitzt, müsste den Kunden suchen, die
 * Akte öffnen und scrollen – währenddessen redet der Kunde weiter. In der
 * Produktionsdatenbank standen deshalb ganze sechs Aufgaben, allesamt aus
 * KI-Empfehlungen, keine einzige aus dem Tagesgeschäft.
 *
 * Deshalb bewusst wenige Felder: Kunde, was zu tun ist, bis wann. Alles andere
 * hat einen Standardwert. Wer mehr eintragen will, kann es danach in der Akte
 * tun – aber niemand muss es, um überhaupt etwas festzuhalten.
 */

// Statt eines Kalenders: die drei Termine, die im Alltag zu 90 % gemeint sind.
function fristen() {
  const heute = new Date();
  const tag = (n) => {
    const d = new Date(heute);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  return [
    { id: "heute", label: "Heute", datum: tag(0) },
    { id: "morgen", label: "Morgen", datum: tag(1) },
    { id: "woche", label: "In einer Woche", datum: tag(7) },
    { id: "ohne", label: "Ohne Datum", datum: null },
  ];
}

export default function AufgabeAnlegen({ kunde: vorgabe = null, onAngelegt, knopf = "voll" }) {
  const [offen, setOffen] = useState(false);
  // Wer die Aufgabe uebernimmt. Leer = ich selbst (setzt der Server).
  // Ohne dieses Feld landete jede Aufgabe beim Ersteller - wer am Telefon
  // etwas fuer einen Kollegen aufnimmt, musste ihm die Aufgabe muendlich
  // weitergeben, und genau dabei geht es verloren.
  const [verantwortlich, setVerantwortlich] = useState("");
  const [namen, setNamen] = useState([]);
  const [kunde, setKunde] = useState(vorgabe);
  const [text, setText] = useState("");
  const [notiz, setNotiz] = useState("");
  const [frist, setFrist] = useState("morgen");
  const [art, setArt] = useState("kundenservice");
  const [speichert, setSpeichert] = useState(false);

  const FRISTEN = fristen();

  const zuruecksetzen = () => {
    setKunde(vorgabe); setText(""); setNotiz(""); setFrist("morgen");
    setArt("kundenservice"); setVerantwortlich("");
  };

  useEffect(() => {
    if (!offen || namen.length) return;
    api.get("/telefonate-bearbeiter/liste")
      .then((r) => setNamen(r.data?.namen || r.data || []))
      .catch(() => {});
  }, [offen, namen.length]);

  const speichern = async () => {
    if (!kunde) { toast.error("Bitte einen Kunden auswählen."); return; }
    if (!text.trim()) { toast.error("Bitte eintragen, was zu tun ist."); return; }

    setSpeichert(true);
    try {
      const gewaehlt = FRISTEN.find((f) => f.id === frist);
      await api.post(`/customers/${kunde.customer_id}/followup`, {
        title: text.trim(),
        description: notiz.trim() || null,
        due_date: gewaehlt?.datum || null,
        status: "Open",
        art,
        owner: verantwortlich || null,
      });
      toast.success("Aufgabe angelegt.", {
        description: `${kunde.name}${gewaehlt?.datum ? ` – fällig ${gewaehlt.label.toLowerCase()}` : ""}`,
      });
      setOffen(false);
      zuruecksetzen();
      onAngelegt?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aufgabe konnte nicht angelegt werden.");
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <>
      {knopf === "symbol" ? (
        // Sinnbild plus Wort: Ein Listen-Plus allein erkannte niemand als
        // "Aufgabe anlegen". Auf dem Telefon bleibt nur das Sinnbild, sonst
        // wird die Kopfzeile zu eng für die Suche.
        <button onClick={() => setOffen(true)} title="Aufgabe anlegen"
                data-testid="aufgabe-anlegen-knopf"
                className="h-9 rounded-lg flex items-center gap-1.5 px-2 md:px-3
                           text-slate-600 hover:bg-slate-100 hover:text-slate-900 shrink-0
                           lg:border lg:border-slate-200 lg:bg-white lg:shadow-sm lg:hover:bg-slate-50 transition-colors">
          <ListPlus className="h-[18px] w-[18px]" />
          <span className="hidden lg:inline text-sm font-semibold whitespace-nowrap">Aufgabe anlegen</span>
        </button>
      ) : (
        <Button variant="outline" className="h-9" onClick={() => setOffen(true)}
                data-testid="aufgabe-anlegen-knopf">
          <ListPlus className="h-4 w-4 mr-1.5" /> Aufgabe
        </Button>
      )}

      <Dialog open={offen} onOpenChange={(o) => { setOffen(o); if (!o) zuruecksetzen(); }}>
        <DialogContent data-testid="aufgabe-dialog">
          <DialogHeader><DialogTitle>Aufgabe anlegen</DialogTitle></DialogHeader>

          <div className="space-y-3">
            {!vorgabe && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Für welchen Kunden?</label>
                <KundenAuswahl wert={kunde} onWahl={setKunde} autoFocus testId="aufgabe-kunde" />
              </div>
            )}
            {vorgabe && (
              <div className="text-sm text-slate-600">
                Kunde: <b className="text-slate-900">{vorgabe.name}</b>
              </div>
            )}

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Was ist zu tun?</label>
              <Input value={text} onChange={(e) => setText(e.target.value)}
                     placeholder="z.B. Rückruf wegen Lieferung Brücke 14"
                     className="h-10 md:h-9" data-testid="aufgabe-text"
                     autoFocus={!!vorgabe}
                     onKeyDown={(e) => { if (e.key === "Enter") speichern(); }} />
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Bis wann?</label>
              <div className="flex flex-wrap gap-1.5" data-testid="aufgabe-fristen">
                {FRISTEN.map((f) => (
                  <button key={f.id} type="button" onClick={() => setFrist(f.id)}
                          data-testid={`aufgabe-frist-${f.id}`}
                          className={`rounded-md border px-2.5 py-1.5 text-sm transition-colors ${
                            frist === f.id ? "border-slate-900 bg-slate-900 text-white"
                                           : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Art</label>
              <div className="flex gap-1.5" data-testid="aufgabe-arten">
                {[["kundenservice", "Kundenservice"], ["vertrieb", "Vertrieb"]].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setArt(id)}
                          data-testid={`aufgabe-art-${id}`}
                          className={`rounded-md border px-2.5 py-1.5 text-sm transition-colors ${
                            art === id ? "border-slate-900 bg-slate-900 text-white"
                                       : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <details className="text-sm">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                Notiz hinzufügen (freiwillig)
              </summary>
              <Textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2}
                        placeholder="Worum ging es genau?"
                        className="mt-1.5" data-testid="aufgabe-notiz" />
            </details>
          </div>

          <div className="pt-1">
            <label className="text-xs text-slate-500 mb-1 block">Verantwortlich</label>
            <div className="flex flex-wrap gap-1.5" data-testid="aufgabe-verantwortlich">
              <button type="button" onClick={() => setVerantwortlich("")}
                      className={`text-xs px-2.5 py-1.5 rounded-md border transition
                                 ${!verantwortlich
                                   ? "border-slate-900 bg-slate-900 text-white"
                                   : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                Ich selbst
              </button>
              {namen.map((n) => {
                const name = typeof n === "string" ? n : n.name;
                return (
                  <button key={name} type="button" onClick={() => setVerantwortlich(name)}
                          data-testid={`verantwortlich-${name}`}
                          className={`text-xs px-2.5 py-1.5 rounded-md border transition
                                     ${verantwortlich === name
                                       ? "border-slate-900 bg-slate-900 text-white"
                                       : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                    {name}
                  </button>
                );
              })}
            </div>
            {verantwortlich && (
              <p className="text-[11px] text-slate-400 mt-1">
                {verantwortlich} sieht die Aufgabe in seiner Liste und im Zähler.
                Bleibt sie länger als drei Tage überfällig, erscheint sie bei allen.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="h-9" onClick={() => setOffen(false)}>Abbrechen</Button>
            <Button className="h-9" onClick={speichern} disabled={speichert}
                    data-testid="aufgabe-speichern">
              {speichert && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
